import type {
  PoFieldDefinitionDetailFragment,
  PoFieldValueDetailFragment,
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { fieldLabel } from 'modules/procurement/components/poTemplates/FieldEditor/translations'

import type {
  LineItemDraft,
  LineItemFieldValueDraft,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'

export type HeaderSectionType = 'general_information' | 'vendor_contact' | 'notes_and_delivery'

const HEADER_SECTIONS: HeaderSectionType[] = [
  'general_information',
  'vendor_contact',
  'notes_and_delivery',
]

export interface FieldWithValue {
  definition: PoFieldDefinitionDetailFragment
  value: string | null
}

export interface FooterTotal {
  label: string
  amountCents: number
}

function isHeaderSectionType(value: string): value is HeaderSectionType {
  switch (value) {
    case 'general_information':
    case 'vendor_contact':
    case 'notes_and_delivery':
      return true
    default:
      return false
  }
}

export function groupHeaderFields(
  definitions: PoFieldDefinitionDetailFragment[],
  values: PoFieldValueDetailFragment[]
): Map<HeaderSectionType, FieldWithValue[]> {
  const valueByDefinitionId = new Map<string, string | null>()
  for (const fv of values) {
    if (!fv.fieldDefinition) continue
    valueByDefinitionId.set(fv.fieldDefinition.id, fv.value ?? null)
  }

  const grouped = new Map<HeaderSectionType, FieldWithValue[]>()
  for (const section of HEADER_SECTIONS) {
    grouped.set(section, [])
  }

  const sorted = [...definitions].sort((a, b) => a.position - b.position)
  for (const def of sorted) {
    if (!def.visible) continue
    if (def.sectionType === 'line_item_columns') continue
    if (!isHeaderSectionType(def.sectionType)) continue

    const sectionFields = grouped.get(def.sectionType)
    if (sectionFields) {
      sectionFields.push({
        definition: def,
        value: valueByDefinitionId.get(def.id) ?? null,
      })
    }
  }

  return grouped
}

function formatBooleanValue(value: string): string {
  if (value === 'true' || value === '1') {
    return i18n.t('procurement.purchase_orders.detail.field_values.boolean_yes')
  }
  if (value === 'false' || value === '0') {
    return i18n.t('procurement.purchase_orders.detail.field_values.boolean_no')
  }
  return value
}

function formatPercentageValue(value: string): string {
  return `${value}%`
}

function translatePaymentMethod(value: string): string {
  switch (value) {
    case 'banktransfer':
    case 'bank_transfer':
      return i18n.t('procurement.purchase_orders.detail.payment.payment_types.bank_transfer')
    case 'cash':
      return i18n.t('procurement.purchase_orders.detail.payment.payment_types.manual_payment')
    case 'virtualcard':
    case 'virtual_card':
      return i18n.t('procurement.purchase_orders.detail.payment.payment_types.single_use_card')
    default:
      return value
  }
}

function resolveEntityValue(
  field: FieldWithValue,
  purchaseOrder: PurchaseOrderDetailFieldsFragment
): string | null {
  const fieldKey = field.definition.fieldKey
  if (!fieldKey) return null

  switch (fieldKey) {
    case 'vendor':
      return purchaseOrder.vendor?.name ?? null
    case 'legal_entity':
      return purchaseOrder.legalEntity.legalName ?? null
    case 'requester':
      return purchaseOrder.purchaseRequest?.requesterEmployee?.fullName ?? null
    case 'currency':
      return purchaseOrder.cost.currency
    case 'payment_terms':
      return field.value ?? null
    case 'payment_method': {
      const raw = field.value ?? purchaseOrder.preferredPaymentMethod
      if (!raw) return null
      return translatePaymentMethod(raw)
    }
    default:
      return null
  }
}

export function isEntityFieldType(fieldType: string): boolean {
  switch (fieldType) {
    case 'currency':
    case 'legal_entity':
    case 'vendor':
    case 'employee':
    case 'team':
    case 'cost_center':
    case 'project':
    case 'payment_method':
    case 'tax_rate':
      return true
    default:
      return false
  }
}

export function displayValue(
  field: FieldWithValue,
  purchaseOrder: PurchaseOrderDetailFieldsFragment
): string {
  if (field.value === null || field.value === '') {
    return ''
  }

  const fieldType = field.definition.fieldType

  switch (fieldType) {
    case 'date':
      return dateBackendToUI(field.value)
    case 'boolean':
      return formatBooleanValue(field.value)
    case 'percentage':
      return formatPercentageValue(field.value)
    default:
      break
  }

  if (isEntityFieldType(fieldType)) {
    const resolved = resolveEntityValue(field, purchaseOrder)
    if (resolved) return resolved
  }

  return field.value
}

export function resolveFieldLabel(definition: PoFieldDefinitionDetailFragment): string {
  if (definition.predefined) {
    return fieldLabel(definition.label)
  }
  return definition.label
}

export function statusBadgeVariant(
  status: ProcurementValueObjectsPurchaseOrderStatusEnum
): 'neutral' | 'info' | 'warning' {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'ordered':
    case 'partial':
    case 'received':
      return 'info'
    case 'processing':
    case 'draft':
    case 'closed':
      return 'neutral'
    default:
      return status satisfies never
  }
}

export function statusLabel(status: ProcurementValueObjectsPurchaseOrderStatusEnum): string {
  switch (status) {
    case 'processing':
      return i18n.t('procurement.purchase_orders.status.processing')
    case 'draft':
      return i18n.t('procurement.purchase_orders.status.draft')
    case 'pending':
      return i18n.t('procurement.purchase_orders.status.pending')
    case 'ordered':
      return i18n.t('procurement.purchase_orders.status.ordered')
    case 'partial':
      return i18n.t('procurement.purchase_orders.status.partial')
    case 'received':
      return i18n.t('procurement.purchase_orders.status.received')
    case 'closed':
      return i18n.t('procurement.purchase_orders.status.closed')
    default:
      return status satisfies never
  }
}

export function isMonetaryFieldKey(fieldKey: string): boolean {
  switch (fieldKey) {
    case 'unit_price':
    case 'discount_amount':
    case 'subtotal':
    case 'tax_amount':
    case 'total':
      return true
    default:
      return false
  }
}

export function formatLineItemCellValue(
  value: string | null,
  definition: PoFieldDefinitionDetailFragment,
  locale: string,
  currency: string,
  MoneyWithCurrency: (opts: { amount: number; currency: string; locale: string }) => string,
  displayValue?: string | null
): string {
  if (value === null || value === '') return ''

  const fieldKey = definition.fieldKey
  const fieldType = definition.fieldType

  if (fieldKey && isMonetaryFieldKey(fieldKey)) {
    const cents = parseInt(value, 10)
    if (!isNaN(cents)) {
      return MoneyWithCurrency({
        amount: cents,
        currency,
        locale,
      })
    }
  }

  if (fieldKey === 'discount_percentage' || fieldType === 'percentage') {
    return `${value}%`
  }

  // Entity-type fields: show displayValue (resolved name) if available
  if (isEntityFieldType(fieldType)) {
    return displayValue ?? value
  }

  switch (fieldType) {
    case 'date':
      return dateBackendToUI(value)
    case 'boolean':
      return formatBooleanValue(value)
    default:
      return value
  }
}

export function buildFooterTotals(
  purchaseOrder: PurchaseOrderDetailFieldsFragment,
  totalsLabel: (key: 'net' | 'tax' | 'total') => string
): FooterTotal[] {
  const subtotalCents = purchaseOrder.subtotal ? Number(purchaseOrder.subtotal) : null
  const taxTotalCents = purchaseOrder.taxTotal ? Number(purchaseOrder.taxTotal) : null
  const totalCents = Number(purchaseOrder.cost.cents)

  const totals: FooterTotal[] = []

  if (subtotalCents !== null) {
    totals.push({ label: totalsLabel('net'), amountCents: subtotalCents })
  }
  if (taxTotalCents !== null) {
    totals.push({ label: totalsLabel('tax'), amountCents: taxTotalCents })
  }
  totals.push({ label: totalsLabel('total'), amountCents: totalCents })

  return totals
}

export function buildFooterTotalsFromDrafts(
  drafts: LineItemDraft[],
  allDefinitions: PoFieldDefinitionDetailFragment[],
  totalsLabelFn: (key: 'net' | 'tax' | 'total') => string
): FooterTotal[] {
  const keyToDefId = new Map<string, string>()
  for (const def of allDefinitions) {
    if (def.fieldKey && def.sectionType === 'line_item_columns') {
      keyToDefId.set(def.fieldKey, def.id)
    }
  }

  const subtotalDefId = keyToDefId.get('subtotal')
  const taxAmountDefId = keyToDefId.get('tax_amount')
  const totalDefId = keyToDefId.get('total')

  let netSum = 0
  let taxSum = 0
  let totalSum = 0

  for (const draft of drafts) {
    for (const fv of draft.fieldValues) {
      const cents = parseInt(fv.value, 10)
      if (isNaN(cents)) continue

      if (subtotalDefId && fv.fieldDefinitionId === subtotalDefId) {
        netSum += cents
      } else if (taxAmountDefId && fv.fieldDefinitionId === taxAmountDefId) {
        taxSum += cents
      } else if (totalDefId && fv.fieldDefinitionId === totalDefId) {
        totalSum += cents
      }
    }
  }

  const totals: FooterTotal[] = []
  if (subtotalDefId) {
    totals.push({ label: totalsLabelFn('net'), amountCents: netSum })
  }
  if (taxAmountDefId) {
    totals.push({ label: totalsLabelFn('tax'), amountCents: taxSum })
  }
  totals.push({ label: totalsLabelFn('total'), amountCents: totalSum })

  return totals
}

export function isNumericFieldType(fieldType: string): boolean {
  switch (fieldType) {
    case 'number':
    case 'integer':
    case 'percentage':
    case 'currency':
    case 'tax_rate':
      return true
    default:
      return false
  }
}

export function isEditableColumn(definition: PoFieldDefinitionDetailFragment): boolean {
  if (definition.computed) return false
  if (definition.fieldKey === 'line_number') return false
  return true
}

export function recomputeLineItemFields(
  fieldValues: LineItemFieldValueDraft[],
  definitions: PoFieldDefinitionDetailFragment[],
  editedFieldKey?: string,
  taxRatePercentage?: number
): LineItemFieldValueDraft[] {
  const defByKey = new Map(definitions.map((d) => [d.fieldKey, d]))

  const getVal = (key: string): number => {
    const def = defByKey.get(key)
    if (!def) return 0
    const fv = fieldValues.find((v) => v.fieldDefinitionId === def.id)
    return Number(fv?.value) || 0
  }

  const result = [...fieldValues]

  const setVal = (key: string, value: number, isMoney: boolean) => {
    const def = defByKey.get(key)
    if (!def) return
    const strValue = isMoney ? String(Math.round(value)) : String(Math.round(value * 100) / 100)
    const idx = result.findIndex((fv) => fv.fieldDefinitionId === def.id)
    if (idx >= 0) {
      result[idx] = { fieldDefinitionId: def.id, value: strValue }
    } else {
      result.push({ fieldDefinitionId: def.id, value: strValue })
    }
  }

  const quantity = getVal('quantity')
  const unitPrice = getVal('unit_price')
  const taxRate = taxRatePercentage ?? 0

  const subtotal = Math.round(quantity * unitPrice)
  setVal('subtotal', subtotal, true)

  // Bidirectional discount: if the user edited discount_amount, derive
  // discount_percentage from it; otherwise compute discount_amount from %.
  let discountAmount: number
  if (editedFieldKey === 'discount_amount') {
    discountAmount = Math.round(getVal('discount_amount'))
    const derivedPercentage = subtotal !== 0 ? (discountAmount / subtotal) * 100 : 0
    setVal('discount_percentage', derivedPercentage, false)
  } else {
    const discountPercentage = getVal('discount_percentage')
    discountAmount = Math.round((subtotal * discountPercentage) / 100)
    setVal('discount_amount', discountAmount, true)
  }

  const taxable = subtotal - discountAmount
  const taxAmount = Math.round((taxable * taxRate) / 100)
  const total = taxable + taxAmount

  setVal('tax_amount', taxAmount, true)
  setVal('total', total, true)

  return result
}
